import React, { useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { apiFetch } from "../../api/client";
import { ME_QUERY_KEY } from "../../hooks/queries/useMe";
import { getToken } from "../../api/token";

// Public route outside auth/profile gates: email links also work while signed in.
// Explicit confirmation avoids consuming a token when a mail scanner opens it.
export default function VerifyEmailRoute() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  return <VerifyEmailScreen key={token} token={token} />;
}

function VerifyEmailScreen({ token }) {
  const queryClient = useQueryClient();
  const busy = useRef(false);
  const [state, setState] = useState("ready");
  const [message, setMessage] = useState("");
  const [resendState, setResendState] = useState("ready");
  const verify = async () => {
    if (busy.current || state === "success" || !token) return;
    busy.current = true;
    setState("pending");
    try {
      await apiFetch("/auth/verify-email", { method: "POST", auth: false, body: { token } });
      setState("success");
      queryClient.invalidateQueries({ queryKey: ME_QUERY_KEY });
    } catch (error) {
      setState("error");
      setMessage(error.status === 401 ? "El enlace es inválido, ya fue utilizado o ha expirado." : "No pudimos verificar tu correo. Inténtalo de nuevo.");
    } finally { busy.current = false; }
  };
  const resend = async () => {
    if (busy.current || resendState === "sent") return;
    busy.current = true;
    setResendState("pending");
    try {
      await apiFetch("/auth/resend-verification", { method: "POST" });
      setResendState("sent");
    } catch { setResendState("error"); }
    finally { busy.current = false; }
  };
  return (
    <main className="min-h-screen flex items-center justify-center bg-white px-6 py-12">
      <div className="w-full max-w-md space-y-6 text-center">
        <h1 className="text-3xl font-bold text-blue-950">{state === "success" ? "Correo verificado" : "Verifica tu correo"}</h1>
        <p role="status" aria-live="polite" className="text-gray-600">
          {state === "success" ? "Confirmamos que este correo te pertenece." : !token ? "Falta el enlace de verificación." : state === "error" ? message : "Confirma tu correo con el botón. Consulta la fecha de vencimiento en el correo."}
        </p>
        {token && state !== "success" && <button type="button" onClick={verify} disabled={state === "pending"} className="bg-blue-950 text-white px-6 py-3 rounded-lg disabled:opacity-60">
          {state === "pending" ? "Verificando…" : "Verificar correo"}
        </button>}
        {state !== "success" && <div>
          {getToken() ? <><button type="button" onClick={resend} disabled={resendState === "pending" || resendState === "sent"} className="min-h-11 px-4 py-3 text-blue-900 underline disabled:opacity-60">Solicitar otro enlace</button>
            <p role="status">{resendState === "sent" ? "Solicitud recibida. Revisa tu correo; las solicitudes recientes se agrupan para evitar duplicados." : resendState === "error" ? "No pudimos solicitar el enlace. Inténtalo más tarde." : ""}</p></> : <p>Inicia sesión para solicitar otro enlace.</p>}
        </div>}
        <Link to="/" className="block min-h-11 px-4 py-3 text-blue-900 underline">{getToken() ? "Continuar a mi cuenta" : "Ir a iniciar sesión"}</Link>
      </div>
    </main>
  );
}
